import React, { DragEvent, FC, useRef, useState } from 'react';
import * as Styled from './styles';
import handleKeyPress from '../../common/handleKeyPress';
import Spinner from './Spinner';

type DiscProps =
    | {
          handleUpload: (image: File) => void;
          /** Whether to show the image upload button/dropzone.
           * If false, this component will only show the error message and spinner. */
          showUploader: true;
      }
    | {
          handleUpload?: (image: File) => void;
          /** Whether to show the image upload button/dropzone.
           * If false, this component will only show the error message and spinner. */
          showUploader: false;
      };
type CommonProps = {
    errorMessage: string;
    fileName?: string;
    isUploading: boolean;
};
type Props = DiscProps & CommonProps;

/** "Modal" for uploading a single image */
const UploadingImage: FC<Props> = ({
    isUploading,
    fileName = '',
    handleUpload,
    errorMessage,
    showUploader = false,
}) => {
    const [isDraggedOver, setIsDraggedOver] = useState(false);
    const fileInput = useRef<HTMLInputElement>(null);

    if (errorMessage) {
        return (
            <Styled.Dropzone>
                <Styled.ErrorIcon icon="exclamation-circle" />
                <Styled.FilenameText isError>{fileName}</Styled.FilenameText>
                <Styled.ScanningText>{errorMessage}</Styled.ScanningText>
            </Styled.Dropzone>
        );
    }
    if (!showUploader && isUploading) {
        return (
            <Styled.Dropzone>
                <Spinner />
                <Styled.FilenameText>{fileName}</Styled.FilenameText>
                <Styled.ScanningText>Scanning and uploading...</Styled.ScanningText>
            </Styled.Dropzone>
        );
    }
    if (showUploader) {
        return (
            <>
                <Styled.Dropzone
                    isDraggedOver={isDraggedOver}
                    onDragOver={() => setIsDraggedOver(true)}
                    onDragEnter={() => setIsDraggedOver(true)}
                    onDragLeave={() => setIsDraggedOver(false)}
                    onDrop={(event: DragEvent<HTMLDivElement>) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setIsDraggedOver(false);
                        if (event.dataTransfer?.files) {
                            const image = event.dataTransfer.files.item(0);
                            handleUpload(image);
                        }
                    }}
                >
                    {isUploading ? (
                        <>
                            <Spinner />
                            <Styled.FilenameText>{fileName}</Styled.FilenameText>
                            <Styled.ScanningText>Scanning and uploading...</Styled.ScanningText>
                        </>
                    ) : (
                        <>
                            <Styled.CloudUploadIcon icon="cloud-upload" />
                            <Styled.UploadText>
                                Drag and drop or{' '}
                                <Styled.BrowseFileLabel
                                    htmlFor="file-input"
                                    onKeyDown={(e) => {
                                        handleKeyPress(e, () => fileInput.current?.click());
                                    }}
                                    tabIndex={0}
                                >
                                    browse for files
                                </Styled.BrowseFileLabel>
                            </Styled.UploadText>
                        </>
                    )}
                </Styled.Dropzone>
                <input
                    type="file"
                    hidden
                    id="file-input"
                    accept="image/png,image/jpg,image/jpeg,image/gif"
                    ref={fileInput}
                    onChange={(event) => {
                        const image = event.target.files?.item(0);
                        if (image) {
                            handleUpload(image);
                        }
                    }}
                />
            </>
        );
    }
    return null;
};

export default UploadingImage;
